import DOMPurify from 'dompurify';
import { track } from '@meltwater/event-tracker';
import { GydaDocument } from '../../gyda-document/index.js';
import { PrDocumentCardHeader } from '../header/index.js';
import {
    CUSTOM_KEY_METRIC,
    DEFAULT_KEY_METRIC,
    KeyMetric,
    KeyMetricTranslations
} from '../../key-metric';
import { logger } from '../../logger';
import { PREMIUM_DOCUMENT_EVENT_NAME } from '../../premium-document-handler/index.js';
import { PrDocumentCardSentiment } from '../sentiment/index.js';
import { TranslationComponent } from '../../translations/translation-component.js';
import {
    prDocumentVisualizationsSeriesChartLabelsEngagement,
    prDocumentVisualizationsSeriesChartLabelsSocialEcho
} from '../../translations/translation-keys.js';
import { version } from '../../version.js';
import {getDocumentKeyMetricValue} from '../../get-document-key-metric-value/index.js';
import { MediaTypes } from '../../media-type-translation/index.js';

const CLEAN_TWITTER_OPERATORS_MATCH = /^(RT|QT)\s+?/;
const DOCUMENT_OPERATION_TABLE = 'document_operation';
const DOCUMENT_OPERATION_LABEL_OPEN = 'open';
const DOCUMENT_OPERATION_SENTIMENT = {
    'p': 'positive',
    'v': 'negative',
    'n': 'neutral',
    'u': 'not_rated'
};

const [ MAJOR_VERSION ] = version.split('.');

const NO_DOCUMENT = Symbol('NO DOCUMENT SET');
const NO_KEY = Symbol('NO KEY SET');
const NO_VALUE = Symbol('NO VALUE SET');
const SOURCE_ID_FACEBOOK = 'social:facebook';
const SOURCE_ID_TWITTER = 'social:twitter';

export const DOCUMENT_GRID_ITEM_CLICK_EVENT_NAME = 'document-grid-item-click';

function isHostedContentProvider(document) {
    if (document.disableInAppOpening) {
        return false;
    }

    return document.isHosted === true || document.isSharingDisabled === true;
}

/**
 * This is the internal document card used by other visualizations.
 * It is intended to be adapted to match the MWV API and made public as a
 * visualization export.
 * @private
 */
export class PrDocumentCard extends HTMLElement {
    #container;
    #document = NO_DOCUMENT;
    #documentUrl = '';
    #key = NO_KEY;
    #language = 'en';
    #keyMetric = DEFAULT_KEY_METRIC;
    #keyMetricLabel = '';
    #title = '';
    #titleOnly = false;
    #value = NO_VALUE;

    static get tagName() {
        return `pr-document-card-v${MAJOR_VERSION}`;
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        this.shadowRoot.innerHTML = /*html*/`
            <style>
                :host {
                    display: flex;
                    background-color: var(--flux-color-white);
                }

                .container {
                    display: flex;
                    width: 100%;
                    overflow: hidden;
                    flex-direction: column;
                }

                .media-container {
                    background-color: var(--flux-color-gray-200);
                    display: flex;
                    align-items: center;
                    height: 33%;
                    overflow-y: hidden;
                    flex: 0 1 auto;

                }
                .media-container > flux-content-card-media {
                    width: 100%;
                }

                .content-container {
                    display: flex;
                    flex-direction: column;
                    flex: 1 1 auto;
                    font-family: var(--flux-font-family-base);
                    padding: var(--flux-spacing-2);

                    & .content {
                        display: flex;
                        flex: 1;
                        flex-direction: column;
                    }
                }

                .source-avatar {
                    height: 36px;
                    width: 36px;
                    flex-shrink: 0;
                }

                .content-header {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }

                .content em {
                    color: #fd005a;
                    font-style: normal;
                    font-weight: var(--flux-font-weight-regular);
                }

                .title {
                    font-family: var(--flux-font-family-heading);
                    font-size: 18px;
                    font-weight: var(--flux-font-weight-bold);
                    line-height: 1.25em;
                    margin: 8px 0 4px 0;

                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: var(--line-clamp, 2);
                    white-space: normal;
                    overflow: hidden;
                }
                .title > a {
                    color: var(--flux-color-gray-800);
                    text-decoration: none;
                }
                .title > a:hover {
                    text-decoration: underline;
                }

                .content.title-only .title {
                    font-weight: var(--flux-font-weight-regular);
                    -webkit-line-clamp: var(--line-clamp, 4);
                }
                .content.title-only .match-sentence {
                    display: none;
                }

                .match-sentence {
                    font-size: 14px;
                    margin: 4px 0 8px 0;

                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: var(--line-clamp, 2);
                    overflow: hidden;
                }

                .footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 12px;
                    gap: 4px;
                    line-height: 14px;
                    font-weight: var(--flux-font-weight-medium);
                    color: var(--flux-color-gray-700);
                }
                .footer > .metadata {
                    display: flex;
                    align-items: center;
                    align-self: stretch;
                    gap: 8px;
                }
                .footer .metric-container flux-icon {
                    vertical-align: bottom;
                }
            </style>
            <article class="container"></article>
        `;

        this.#container = this.shadowRoot.querySelector('.container');
    }

    #getDocumentValue(keyMetric) {
        return getDocumentKeyMetricValue({
            document: this.#document,
            keyMetric,
            language: this.#language
        });
    }

    /**
     * The bottom left corner of the card should show the "key metric" for
     * the analytic. That metric is the measure in any case where it is not
     * already visible somewhere else on the card (e.g. estimated views); in
     * any other scenario, the key metric is the document's reach.
     * @private
     * @returns {undefined}
     */
    get #keyMetricHtml () {
        let keyMetric = this.#keyMetric;

        const isMeasureElsewhereOnCard = [
            KeyMetric.broadcastViewership,
            KeyMetric.editorialReach,
            KeyMetric.engagement,
            KeyMetric.reach,
            KeyMetric.sentiment,
            KeyMetric.socialEcho,
            KeyMetric.socialReach
        ].includes(this.#keyMetric);

        if (isMeasureElsewhereOnCard || this.#keyMetric === DEFAULT_KEY_METRIC) {
            keyMetric = KeyMetric.reach;
        }

        let interiorHTML;
        try {
            const label = keyMetric !== CUSTOM_KEY_METRIC
                ? `<${TranslationComponent.tagName} key="${KeyMetricTranslations[keyMetric]}" language="${this.#language}"></${TranslationComponent.tagName}>`
                : this.#keyMetricLabel;

            const value = keyMetric !== CUSTOM_KEY_METRIC
                ? this.#getDocumentValue(keyMetric)
                : this.#value;

            interiorHTML = typeof value === 'string'
                ? value
                : `<label class="key-metric">
                      <flux-truncated-number number="${value}"></flux-truncated-number>
                      ${label}
                  </label>`;
        }
        catch (error) {
            logger.error('Error rendering document', error.message, error.stack);
            interiorHTML = '—';
        }

        return /*html*/`
            <span id="key-metric" class="metric-container">
                ${interiorHTML}
            </span>`;
    }

    /**
     * @returns {undefined}
     */
    async setData({
        companyId,
        document,
        key,
        keyMetric,
        keyMetricLabel,
        language,
        value
    }) {
        this.#document = document ? new GydaDocument(document) : NO_DOCUMENT;
        this.#key = key ?? NO_KEY;
        this.#keyMetric = keyMetric ?? DEFAULT_KEY_METRIC;
        this.#keyMetricLabel = keyMetricLabel;
        this.#language = language;
        this.#value = value ?? NO_VALUE;

        this.#title = this.#key;
        this.#titleOnly = false;

        if (this.#document !== NO_DOCUMENT && this.#document.title) {
            this.#title = this.#document.title;
        }
        else {
            this.#titleOnly = true;

            if (this.#title === NO_KEY && this.#document !== NO_DOCUMENT) {
                if (this.#document.matchSentence && this.#document.matchSentence.length > 0) {
                    this.#title = this.#document.matchSentence;
                } else {
                    this.#title = this.#document.body;
                }
            }
        }

        if (this.#document.sourceId === SOURCE_ID_FACEBOOK || this.#document.mediaType === MediaTypes.facebook) {
            this.#titleOnly = true;
            this.#title = this.#document.body;
        }

        if (this.#document.sourceId === SOURCE_ID_TWITTER && typeof this.#title === 'string') {
            this.#title = this.#title.replace(CLEAN_TWITTER_OPERATORS_MATCH, '');
        }

        this.#documentUrl = this.#document.paywallUrl || this.#document.url;
        if (isHostedContentProvider(this.#document)) {
            let sharedDocumentViewerUrl = new URL(`https://app.meltwater.com/api/ucs/shareddocumentviewer/${this.#document.documentId}`);
            if (location.hostname.includes('meltwater.net')) {
                sharedDocumentViewerUrl = new URL(`https://staging.meltwater.net/api/ucs/shareddocumentviewer/${this.#document.documentId}`);
            }

            sharedDocumentViewerUrl.searchParams.set('companyId', companyId);
            sharedDocumentViewerUrl.searchParams.set('productType', 'universal-analytics');
            this.#documentUrl = sharedDocumentViewerUrl.toString();
        }

        this.#render();
    }

    #render() {
        if (this.#document === NO_DOCUMENT) {
            this.style.visibility = 'hidden';
        } else {
            this.style.visibility = 'visible';

            this.#container.innerHTML = /*html*/`
                <div class="media-container">
                    <flux-content-card-image
                        link-url="${this.#documentUrl}"
                        image-url="${this.#document.images[0].link}"
                        show-placeholder>
                    </flux-content-card-image>
                </div>
                <div class="content-container">
                    <div class="content-header">
                        <${PrDocumentCardHeader.tagName} language="${this.#language}"></${PrDocumentCardHeader.tagName}>
                    </div>

                    <div class="content">
                        <div class="title">
                            <a target="_blank" href="${this.#documentUrl}">
                                ${DOMPurify.sanitize(this.#title)}
                            </a>
                        </div>

                        <div class="match-sentence">
                            ${DOMPurify.sanitize(this.#document.matchSentence)}
                        </div>
                    </div>

                    <div class="footer">
                        <div class="metadata">
                            ${this.#keyMetricHtml}
                        </div>
                        <${PrDocumentCardSentiment.tagName} language="${this.#language}" sentiment="${this.#document.sentiment}"></${PrDocumentCardSentiment.tagName}>
                    </div>
                </div>
            `;

            this.shadowRoot.querySelector('.content').classList.toggle('title-only', this.#titleOnly);
            this.shadowRoot.querySelector('.title').setAttribute('title', DOMPurify.sanitize(this.#title, { ALLOWED_TAGS: [] }));
            this.shadowRoot.querySelector('.match-sentence').setAttribute('title', DOMPurify.sanitize(this.#document.matchSentence, { ALLOWED_TAGS: [] }));

            this.shadowRoot.querySelector(PrDocumentCardHeader.tagName).document = this.#document;

            const metaDataComponent = this.shadowRoot.querySelector('.metadata');

            if (this.#document.informationType === 'news') {
                const socialEchoNode = document.createElement('span');
                socialEchoNode.classList.add('metric-container');
                socialEchoNode.setAttribute('id', 'social-echo-value');
                socialEchoNode.innerHTML = /*html*/`
                    <label class="social-echo">
                        <flux-truncated-number number="${this.#document.socialEcho}"></flux-truncated-number>
                        <${TranslationComponent.tagName} key="${prDocumentVisualizationsSeriesChartLabelsSocialEcho}" language="${this.#language}"></${TranslationComponent.tagName}>
                    </label>
                `;
                metaDataComponent.append(socialEchoNode);
            }

            if (this.#document.informationType === 'social') {
                const engagementNode = document.createElement('span');
                engagementNode.classList.add('metric-container');
                engagementNode.setAttribute('id', 'engagement-value');
                engagementNode.innerHTML = /*html*/`
                    <flux-icon name="features:comment"></flux-icon>
                    <flux-truncated-number number="${this.#document.engagement}"></flux-truncated-number>
                    <flux-tooltip><${TranslationComponent.tagName} key="${prDocumentVisualizationsSeriesChartLabelsEngagement}" language="${this.#language}"></${TranslationComponent.tagName}></flux-tooltip>
                `;
                metaDataComponent.append(engagementNode);
            }

            const image = this.shadowRoot.querySelector('flux-content-card-image');
            const title = this.shadowRoot.querySelector('.title');
            [image, title].forEach((element) => {
                element.addEventListener('click', (event) => {
                    track(DOCUMENT_OPERATION_TABLE, DOCUMENT_OPERATION_LABEL_OPEN, {
                        // List of fields from
                        // https://github.com/meltwater/flux-content-stream/blob/6c0fca4edd90f4579f49b0fa9a3251189ed934b8/src/utils/analytics/index.ts#L23
                        documentId: this.#document.documentId,
                        documentTitle: this.#document.title,
                        documentUrl: this.#document.url,
                        sentiment: DOCUMENT_OPERATION_SENTIMENT[this.#document.sentiment],
                        isPremium: this.#document.isHosted
                    });

                    if (this.#document.isHosted) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.dispatchEvent(new CustomEvent(PREMIUM_DOCUMENT_EVENT_NAME, {
                            bubbles: true,
                            composed: true,
                            cancelable: true,
                            detail: {
                                documentId: this.#document.documentId,
                                document: this.#document
                            }
                        }));
                    }
                });
            });
        }

    }
}

if (!customElements.get(PrDocumentCard.tagName)) {
    customElements.define(PrDocumentCard.tagName, PrDocumentCard);
}
